import { alpha, type SxProps, type Theme } from '@mui/material'
import { FACILITATOR_COLORS, INTERAX_COLORS } from '../../../../constants/colors'
import { MODAL_Z_INDEX } from '../../../../shared/components/DefaultModal'

export const TOUR_Z_INDEX = MODAL_Z_INDEX
export const TOUR_SCREEN_GUTTER_PX = 16
export const TOUR_PANEL_GAP_PX = 32 // space between the lit menu item and the panel
export const TOUR_PANEL_TOP_OFFSET_PX = 14 // the panel starts a little above the lit menu item
export const TOUR_SPOTLIGHT_PADDING_PX = 4
export const TOUR_ARROW_SIZE_PX = 12
export const TOUR_PANEL_WITH_LIST_WIDTH_PX = 704
export const TOUR_PANEL_CONTENT_WIDTH_PX = 472
const TOUR_LIST_WIDTH_PX = 232
const TOUR_CARD_WIDTH_PX = 460
const TOUR_STOP_NUMBER_SIZE_PX = 20
const TOUR_ICON_TILE_SIZE_PX = 48
const TOUR_BULLET_SIZE_PX = 6

const TOUR_DIM = alpha(INTERAX_COLORS.offBlack, 0.72)
const TOUR_SURFACE = INTERAX_COLORS.slate
const TOUR_LIST_SURFACE = FACILITATOR_COLORS.facilitatorSidebarBackground
const TOUR_HIGHLIGHTS_SURFACE = alpha(INTERAX_COLORS.heritageBlue, 0.75)
const TOUR_PANEL_SHADOW = `0 16px 48px ${alpha(INTERAX_COLORS.offBlack, 0.6)}`

export const tourScreenSx: SxProps<Theme> = { position: 'fixed', inset: 0, outline: 'none' }

export const tourDimSx: SxProps<Theme> = { position: 'fixed', inset: 0, backgroundColor: TOUR_DIM }

export const buildTourSpotlightSx = (rect: DOMRect): SxProps<Theme> => ({
  position: 'fixed',
  top: rect.top - TOUR_SPOTLIGHT_PADDING_PX,
  left: rect.left - TOUR_SPOTLIGHT_PADDING_PX,
  width: rect.width + TOUR_SPOTLIGHT_PADDING_PX * 2,
  height: rect.height + TOUR_SPOTLIGHT_PADDING_PX * 2,
  borderRadius: '10px',
  border: `2px solid ${INTERAX_COLORS.sun}`,
  boxShadow: `0 0 0 9999px ${TOUR_DIM}, 0 0 24px ${INTERAX_COLORS.sun40}`,
  transition: 'top 0.25s ease, height 0.25s ease',
  pointerEvents: 'none'
})

export const tourCenterSx: SxProps<Theme> = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  p: { xs: 2, sm: 2, md: 3 }
}

export const tourCardSx: SxProps<Theme> = {
  width: '100%',
  maxWidth: TOUR_CARD_WIDTH_PX,
  p: { xs: 3, sm: 4, md: 4 },
  borderRadius: '16px',
  backgroundColor: TOUR_SURFACE,
  border: `1px solid ${INTERAX_COLORS.white10}`,
  boxShadow: TOUR_PANEL_SHADOW
}

export const tourIconTileSx: SxProps<Theme> = {
  width: TOUR_ICON_TILE_SIZE_PX,
  height: TOUR_ICON_TILE_SIZE_PX,
  mb: 2.5,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '10px',
  backgroundColor: INTERAX_COLORS.sun20,
  color: INTERAX_COLORS.sun,
  '& svg': { width: 24, height: 24 }
}

const tourTitleSxBase = {
  m: 0,
  fontSize: { xs: '20px', sm: '22px', md: '24px' },
  lineHeight: 1.3,
  fontWeight: 700,
  color: INTERAX_COLORS.white
}

export const tourTitleSx: SxProps<Theme> = tourTitleSxBase

export const tourStopTitleSx: SxProps<Theme> = { ...tourTitleSxBase, mt: 1.5, pr: 4 }

export const tourBodySx: SxProps<Theme> = {
  mt: 1.5,
  fontSize: '14px',
  lineHeight: '22px',
  color: INTERAX_COLORS.white70
}

export const tourActionsSx: SxProps<Theme> = {
  mt: 3,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 1.5
}

const tourPanelBaseSx = {
  position: 'fixed',
  display: 'flex',
  overflow: 'visible',
  borderRadius: '16px',
  border: `1px solid ${INTERAX_COLORS.white10}`,
  backgroundColor: TOUR_SURFACE,
  boxShadow: TOUR_PANEL_SHADOW
} as const

/** Docked at the bottom when no menu entry is on screen; otherwise beside it, narrowed to the window. */
export const buildTourPanelSx = (placement: { top: number; left: number; maxWidth: number } | null): SxProps<Theme> => {
  if (!placement) {
    return {
      ...tourPanelBaseSx,
      left: TOUR_SCREEN_GUTTER_PX,
      right: TOUR_SCREEN_GUTTER_PX,
      bottom: TOUR_SCREEN_GUTTER_PX,
      maxHeight: `calc(100dvh - ${TOUR_SCREEN_GUTTER_PX * 2}px)`,
      overflowY: 'auto'
    }
  }

  const contentWidth = Math.min(TOUR_PANEL_CONTENT_WIDTH_PX, placement.maxWidth)
  return {
    ...tourPanelBaseSx,
    top: placement.top,
    left: placement.left,
    width: {
      xs: contentWidth,
      sm: contentWidth,
      md: contentWidth,
      lg: Math.min(TOUR_PANEL_WITH_LIST_WIDTH_PX, placement.maxWidth)
    }
  }
}

export const buildTourArrowSx = (top: number): SxProps<Theme> => ({
  position: 'absolute',
  top,
  left: -TOUR_ARROW_SIZE_PX / 2,
  width: TOUR_ARROW_SIZE_PX,
  height: TOUR_ARROW_SIZE_PX,
  transform: 'rotate(45deg)',
  backgroundColor: { xs: TOUR_SURFACE, sm: TOUR_SURFACE, md: TOUR_SURFACE, lg: TOUR_LIST_SURFACE },
  borderLeft: `1px solid ${INTERAX_COLORS.white10}`,
  borderBottom: `1px solid ${INTERAX_COLORS.white10}`
})

export const tourListColumnSx: SxProps<Theme> = {
  display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
  width: TOUR_LIST_WIDTH_PX,
  flexShrink: 0,
  px: 1.5,
  py: 2.5,
  borderRadius: '16px 0 0 16px',
  backgroundColor: TOUR_LIST_SURFACE
}

export const tourPlainListSx: SxProps<Theme> = { m: 0, p: 0, listStyle: 'none' }

export const tourListHeadingSx: SxProps<Theme> = {
  px: 1,
  mb: 1.5,
  fontSize: '12px',
  fontWeight: 600,
  color: INTERAX_COLORS.thunderGrey
}

const tourListItemBaseSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  px: 1,
  py: 0.75,
  mb: 0.25,
  borderRadius: '8px',
  fontSize: '13px'
}

export const tourListItemSx: SxProps<Theme> = {
  ...tourListItemBaseSx,
  fontWeight: 400,
  color: INTERAX_COLORS.white70,
  border: '1px solid transparent'
}

export const tourListItemCurrentSx: SxProps<Theme> = {
  ...tourListItemBaseSx,
  fontWeight: 600,
  color: INTERAX_COLORS.white,
  backgroundColor: INTERAX_COLORS.sun20,
  border: `1px solid ${INTERAX_COLORS.sun20}`
}

const tourStopNumberBaseSx = {
  width: TOUR_STOP_NUMBER_SIZE_PX,
  height: TOUR_STOP_NUMBER_SIZE_PX,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  fontSize: '11px',
  fontWeight: 600,
  '& svg': { width: 12, height: 12 }
}

export const tourStopNumberDoneSx: SxProps<Theme> = {
  ...tourStopNumberBaseSx,
  backgroundColor: INTERAX_COLORS.sun20,
  color: INTERAX_COLORS.sun
}

export const tourStopNumberCurrentSx: SxProps<Theme> = {
  ...tourStopNumberBaseSx,
  backgroundColor: INTERAX_COLORS.sun,
  color: INTERAX_COLORS.offBlack
}

export const tourStopNumberUpcomingSx: SxProps<Theme> = {
  ...tourStopNumberBaseSx,
  border: `1px solid ${INTERAX_COLORS.thunderGrey}`,
  color: INTERAX_COLORS.thunderGrey
}

export const tourContentColumnSx: SxProps<Theme> = {
  position: 'relative',
  flex: 1,
  minWidth: 0,
  p: { xs: 2.5, sm: 3, md: 3.5 }
}

export const tourStopCounterSx: SxProps<Theme> = {
  fontSize: '12px',
  fontWeight: 600,
  color: INTERAX_COLORS.sun
}

export const tourCloseButtonSx: SxProps<Theme> = {
  position: 'absolute',
  top: { xs: 12, sm: 16, md: 20 },
  right: { xs: 12, sm: 16, md: 20 },
  color: INTERAX_COLORS.white70,
  '&:hover': { color: INTERAX_COLORS.white, backgroundColor: INTERAX_COLORS.white10 },
  '& svg': { width: 18, height: 18 }
}

export const tourHighlightsSx: SxProps<Theme> = {
  mt: 2,
  px: 1.75,
  py: 1.5,
  borderRadius: '10px',
  backgroundColor: TOUR_HIGHLIGHTS_SURFACE
}

export const tourHighlightsHeadingSx: SxProps<Theme> = {
  mb: 0.75,
  fontSize: '12px',
  fontWeight: 700,
  color: INTERAX_COLORS.white
}

export const tourHighlightSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 1.25,
  py: 0.4,
  fontSize: '13px',
  lineHeight: '19px',
  color: INTERAX_COLORS.white70
}

export const tourBulletSx: SxProps<Theme> = {
  width: TOUR_BULLET_SIZE_PX,
  height: TOUR_BULLET_SIZE_PX,
  flexShrink: 0,
  borderRadius: '50%',
  backgroundColor: INTERAX_COLORS.sun,
  transform: 'translateY(-1px)'
}

/** Skip, Back and Next on one row; on a phone Next gets a row of its own above the other two. */
export const tourFooterSx: SxProps<Theme> = {
  mt: 2.5,
  display: 'grid',
  alignItems: 'center',
  gap: 1.5,
  gridTemplateColumns: { xs: '1fr auto', sm: '1fr auto auto', md: '1fr auto auto' },
  gridTemplateAreas: { xs: '"next next" "skip back"', sm: '"skip back next"', md: '"skip back next"' }
}

export const tourFooterSkipSx: SxProps<Theme> = { gridArea: 'skip', justifySelf: 'start' }
export const tourFooterBackSx: SxProps<Theme> = { gridArea: 'back' }
export const tourFooterNextSx: SxProps<Theme> = { gridArea: 'next' }

export const tourSkipSx: SxProps<Theme> = {
  px: 0.5,
  fontSize: '14px',
  whiteSpace: 'nowrap',
  color: INTERAX_COLORS.white70,
  textTransform: 'none',
  '&:hover': { color: INTERAX_COLORS.white, backgroundColor: 'transparent' }
}

export const tourEntrySx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: { xs: 1, sm: 1.25, md: 1.5 },
  flexShrink: 0
}

export const tourEntryLabelSx: SxProps<Theme> = {
  display: { xs: 'none', sm: 'flex', md: 'flex' },
  alignItems: 'center',
  gap: 1,
  fontSize: '14px',
  color: INTERAX_COLORS.white70,
  whiteSpace: 'nowrap',
  '& svg': { width: 18, height: 18, color: INTERAX_COLORS.sun }
}

export const tourEntryDismissSx: SxProps<Theme> = {
  color: INTERAX_COLORS.white70,
  '&:hover': { color: INTERAX_COLORS.white, backgroundColor: INTERAX_COLORS.white10 },
  '& svg': { width: 18, height: 18 }
}
